import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IeltsAttemptContext } from '../utils/ieltsStart';
import { Base_URL } from '../constants/api';
import {
  buildIeltsDraftKey,
  loadIeltsDraftFromBrowser,
  removeIeltsDraftFromBrowser,
  saveIeltsDraftToBrowser,
} from '../utils/ieltsDraftStorage';

const BACKEND_SAVE_INTERVAL_MS = 5000;

function isBackendDraftNewer(backendDraft, localDraft) {
  if (!backendDraft?.updated_at) return false;
  if (!localDraft?.updatedAt) return true;
  return new Date(backendDraft.updated_at).getTime() > new Date(localDraft.updatedAt).getTime();
}

function mapBackendDraftToState(backendDraft) {
  if (!backendDraft) return null;
  return {
    answers: backendDraft.answers_json || {},
    currentStep: backendDraft.current_step || {},
    timeState: backendDraft.time_state_json || {},
    audioState: backendDraft.audio_state_json || {},
    updatedAt: backendDraft.updated_at,
  };
}

export default function useIeltsAutosave({ enabled = true, restoreEnabled = true, identity, draftState, onRestore, exitPendingRef }) {
  const attempt = useContext(IeltsAttemptContext);
  const draftId = attempt?.draftId;
  const [hydrated, setHydrated] = useState(false);
  const latestPayloadRef = useRef(null);
  const closedDraftKeyRef = useRef(null);
  const pendingExitRef = useRef(null);
  const accessRevisionRef = useRef(attempt?.accessRevision);
  const [exitPending, setExitPending] = useState(false);
  const [exitBusy, setExitBusy] = useState(false);
  const backendTimerRef = useRef(null);
  const lastBackendSaveAtRef = useRef(0);
  const mountedRef = useRef(true);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const scope = identity?.scope || (identity?.fullTestAttemptId ? 'full' : 'sectional');
  const section = identity?.section;
  const testId = identity?.testId;
  const groupId = identity?.groupId;
  const fullTestAttemptId = identity?.fullTestAttemptId;
  const browserDraftKey = useMemo(() => buildIeltsDraftKey({
    scope,
    section,
    testId,
    groupId,
    fullTestAttemptId,
  }), [fullTestAttemptId, groupId, scope, section, testId]);
  const draftKey = attempt?.draftKey || browserDraftKey;
  const active = Boolean(enabled && draftKey && testId && section);

  useEffect(() => {
    accessRevisionRef.current = attempt?.accessRevision;
    pendingExitRef.current = null;
    if (exitPendingRef) exitPendingRef.current = false;
    setExitPending(false);
  }, [draftKey, attempt?.accessRevision, exitPendingRef]);

  useEffect(() => {
    if (!active) return;
    const path = window.location.pathname;
    const closeOnLeave = () => {
      const payload = latestPayloadRef.current;
      if (!payload || closedDraftKeyRef.current === draftKey) return;
      void fetch(`${Base_URL}/app/users/ielts-tests/drafts/exit`, {
        method: 'POST', credentials: 'include', keepalive: true,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ draftId: payload.draftId, draftKey: payload.draftKey, accessRevision: accessRevisionRef.current }),
      }).catch(() => {});
    };
    window.addEventListener('pagehide', closeOnLeave);
    return () => {
      window.removeEventListener('pagehide', closeOnLeave);
      // Switching sections within a full attempt keeps the same route.
      if (window.location.pathname !== path) closeOnLeave();
    };
  }, [active, draftKey]);

  const buildPayload = useCallback((state) => ({
    draftKey,
    ...(draftId ? { draftId } : {}),
    scope,
    section,
    testId,
    groupId: groupId || null,
    fullTestAttemptId: fullTestAttemptId || null,
    answers: state?.answers ?? {},
    currentStep: state?.currentStep ?? {},
    timeState: state?.timeState ?? {},
    audioState: state?.audioState ?? {},
    updatedAt: new Date().toISOString(),
  }), [draftKey, draftId, fullTestAttemptId, groupId, scope, section, testId]);

  const flushBackendSave = useCallback(async (requireSuccess = false) => {
    backendTimerRef.current = null;
    const payload = latestPayloadRef.current;
    if (!active || !payload || closedDraftKeyRef.current === payload.draftKey) return;

    lastBackendSaveAtRef.current = Date.now();
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data?.responseCode !== 200) throw new Error(data?.message || 'Unable to save progress. Please retry.');
      if (accessRevisionRef.current == null) accessRevisionRef.current = data.response?.access_revision;
    } catch (error) {
      if (requireSuccess) throw error;
      // Browser draft remains available. The next state change will retry backend autosave.
    }
  }, [active]);

  const queueBackendSave = useCallback(() => {
    if (!active || backendTimerRef.current) return;
    const elapsed = Date.now() - lastBackendSaveAtRef.current;
    const delay = Math.max(0, BACKEND_SAVE_INTERVAL_MS - elapsed);
    backendTimerRef.current = setTimeout(flushBackendSave, delay);
  }, [active, flushBackendSave]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (backendTimerRef.current) clearTimeout(backendTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreDraft() {
      setHydrated(false);
      if (!active) {
        setHydrated(true);
        return;
      }

      const localDraft = loadIeltsDraftFromBrowser(browserDraftKey);
      if (localDraft?.state && restoreEnabled) {
        onRestoreRef.current?.(localDraft.updatedAt
          ? { ...localDraft.state, updatedAt: localDraft.updatedAt }
          : localDraft.state);
      }

      try {
        const params = new URLSearchParams({
          draftKey,
          scope,
          section,
          testId: String(testId),
        });
        if (groupId) params.set('groupId', String(groupId));
        if (draftId) params.set('draftId', String(draftId));
        if (fullTestAttemptId) params.set('fullTestAttemptId', String(fullTestAttemptId));

        const response = await fetch(`${Base_URL}/app/users/ielts-tests/drafts?${params.toString()}`, {
          credentials: 'include',
        });
        const data = await response.json();
        const backendDraft = data?.response;
        if (accessRevisionRef.current == null) accessRevisionRef.current = backendDraft?.access_revision;
        if (!cancelled && closedDraftKeyRef.current !== draftKey && backendDraft && isBackendDraftNewer(backendDraft, localDraft) && restoreEnabled) {
          onRestoreRef.current?.(mapBackendDraftToState(backendDraft));
        }
      } catch (_) {
        // Local restore already ran. Backend restore will be retried on the next visit.
      } finally {
        if (!cancelled && mountedRef.current) setHydrated(true);
      }
    }

    restoreDraft();
    return () => {
      cancelled = true;
    };
  }, [active, draftKey, browserDraftKey, draftId, fullTestAttemptId, groupId, restoreEnabled, scope, section, testId]);

  useEffect(() => {
    if (!active || !hydrated || !draftState || closedDraftKeyRef.current === draftKey) return;
    const payload = buildPayload(draftState);
    latestPayloadRef.current = payload;
    saveIeltsDraftToBrowser(browserDraftKey, { draftId, draftKey, state: draftState, updatedAt: payload.updatedAt });
    queueBackendSave();
  }, [active, hydrated, draftKey, browserDraftKey, draftId, draftState, buildPayload, queueBackendSave]);

  const clearDraft = useCallback(async () => {
    if (!draftKey) return;
    closedDraftKeyRef.current = draftKey;
    removeIeltsDraftFromBrowser(browserDraftKey);
    latestPayloadRef.current = null;
    if (backendTimerRef.current) {
      clearTimeout(backendTimerRef.current);
      backendTimerRef.current = null;
    }
    if (!active) return;
    try {
      await fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftKey,
          scope,
          section,
          testId,
          groupId: groupId || null,
          fullTestAttemptId: fullTestAttemptId || null,
        }),
      });
    } catch (_) {
      // Backend clear can be retried by final submit cleanup later; local draft is already removed.
    }
  }, [active, draftKey, browserDraftKey, fullTestAttemptId, groupId, scope, section, testId]);

  const saveDraft = useCallback(async (state = draftState, exit = false) => {
    if (exit) {
      if (exitPendingRef) exitPendingRef.current = true;
      setExitPending(true); setExitBusy(true);
    }
    try {
    if (!pendingExitRef.current || pendingExitRef.current.draftKey !== draftKey) {
      latestPayloadRef.current = buildPayload(state);
      await flushBackendSave(true);
    }
    if (exit && active) {
      if (!pendingExitRef.current || pendingExitRef.current.draftKey !== draftKey) {
        pendingExitRef.current = { draftId: latestPayloadRef.current.draftId, draftKey, accessRevision: accessRevisionRef.current };
      }
      // Once exit is sent, the server may already have revoked this draft.
      // A retry must repeat only the idempotent exit, never another save.
      closedDraftKeyRef.current = draftKey;
      clearTimeout(backendTimerRef.current);
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/drafts/exit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingExitRef.current),
      });
      const data = await response.json();
      if (!response.ok || data.responseCode !== 200) throw new Error(data.message || 'Unable to close test access. Please retry.');
      pendingExitRef.current = null;
    }
    } catch (error) {
      if (!pendingExitRef.current) {
        if (exitPendingRef) exitPendingRef.current = false;
        setExitPending(false);
      }
      throw error;
    } finally { if (exit) setExitBusy(false); }
  }, [active, buildPayload, draftKey, draftState, flushBackendSave, exitPendingRef]);

  return { clearDraft, saveDraft, draftKey, draftId, hydrated, exitPending, exitBusy };
}
